import { useSyncExternalStore } from 'react'
import { PLATFORM_FEE_PERCENT, PREMIUM_DISCOUNT_PERCENT } from '@/lib/constants'

/**
 * Taxa da plataforma e desconto Premium em uso no site.
 *
 * Começam com as constantes de `lib/constants` e são trocadas pelos valores de
 * `platform_settings` (platform_fee_percent, premium_discount_percent) assim
 * que o `PlatformRatesSync` os lê. Assim, mudar em Admin → Configurações muda
 * também os textos e as simulações, e não só a cobrança do create-checkout.
 */
export interface PlatformRates {
  feePercent: number
  premiumDiscountPercent: number
}

let rates: PlatformRates = {
  feePercent: PLATFORM_FEE_PERCENT,
  premiumDiscountPercent: PREMIUM_DISCOUNT_PERCENT,
}
const listeners = new Set<() => void>()

export function getPlatformRates(): PlatformRates {
  return rates
}

export function getPlatformFeePercent(): number {
  return rates.feePercent
}

export function getPremiumDiscountPercent(): number {
  return rates.premiumDiscountPercent
}

/** Valores inválidos (vazio, negativo, acima de 100) são ignorados. */
export function setPlatformRates(next: Partial<PlatformRates>): void {
  const valid = (n: number | undefined) =>
    typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100
  const merged: PlatformRates = {
    feePercent: valid(next.feePercent) ? next.feePercent! : rates.feePercent,
    premiumDiscountPercent: valid(next.premiumDiscountPercent)
      ? next.premiumDiscountPercent!
      : rates.premiumDiscountPercent,
  }
  if (
    merged.feePercent === rates.feePercent &&
    merged.premiumDiscountPercent === rates.premiumDiscountPercent
  ) {
    return
  }
  rates = merged
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Taxa e desconto atuais; o componente re-renderiza quando mudam. */
export function usePlatformRates(): PlatformRates {
  return useSyncExternalStore(subscribe, getPlatformRates, getPlatformRates)
}
